import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import NoMatchingRecords from './components/NoMatchingRecords';
import PdfPreviewDialog from './components/PdfPreviewDialog';
import { apiUrl, downloadBlob, handleApiError } from './utils/api';
import { useRole } from '../auth/RoleContext';
import { visiblePlants } from '../auth/permissions';
import { formatDate } from './utils/date';

// Invoice money reads "5,657.75" — with separators, as on the printed sheet.
const money = (value) => `$${Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const number = (value) => Number(value || 0).toFixed(2);

// Plant Invoice: bill a PLANT for printed weigh tickets (ticket_detail) → /api/ivy/plant-invoice.
// ONE ROW PER TICKET, mirroring Ivy's printed sheet (ROE-2026-0010 / CO-2026-0194): the customer
// reconciles each row against the ticket stapled behind it, so this preview shows the same Truck # /
// Ticket No columns and the same date-only-when-it-changes grouping as the PDF. (Customer/field-ticket
// invoicing lives on the Field Ticket Invoice screen.)
export default function IvyPlantInvoice() {
  const { role } = useRole();
  const [plants, setPlants] = useState([]);
  const [form, setForm] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    plantId: '',
    startDate: '',
    endDate: '',
    invoiceFor: '',
    billTo: '',
  });
  const [invoice, setInvoice] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    axios.get(`${apiUrl}/api/master/plants`)
      .then((res) => setPlants(Array.isArray(res.data) ? res.data : []))
      .catch((err) => setError(handleApiError(err, 'Failed to load plants')));
  }, []);

  const handleChange = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const validate = () => {
    if (!form.plantId) { setError('Plant is required'); return false; }
    if (!form.startDate || !form.endDate) { setError('Start and end dates are required'); return false; }
    return true;
  };

  const endpoint = () => `${apiUrl}/api/ivy/plant-invoice`;

  // invoiceFor / billTo are optional: blank lets the backend derive them from the tickets (plant +
  // job half of the code, and the most common customer spelling). Anything typed wins verbatim.
  const params = () => ({
    plantId: form.plantId,
    startDate: form.startDate,
    endDate: form.endDate,
    invoiceNumber: form.invoiceNumber || undefined,
    invoiceDate: form.invoiceDate || undefined,
    invoiceFor: form.invoiceFor || undefined,
    billTo: form.billTo || undefined,
  });

  const generate = async () => {
    setError('');
    setSearched(true);
    setInvoice(null);
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.get(endpoint(), { params: params() });
      setInvoice(response.data);
    } catch (err) {
      setError(handleApiError(err, 'Failed to generate invoice'));
    } finally {
      setLoading(false);
    }
  };

  const pdfName = () => `${(form.invoiceNumber || 'plant-invoice').replace(/[^\w-]+/g, '_')}.pdf`;

  const downloadPdf = async () => {
    setError('');
    if (!validate()) return;
    try {
      const response = await axios.get(`${endpoint()}/pdf`, { params: params(), responseType: 'blob' });
      downloadBlob(response, pdfName());
    } catch (err) {
      setError(handleApiError(err, 'Failed to download invoice PDF'));
    }
  };

  const preview = async () => {
    setError('');
    if (!validate()) return;
    try {
      const response = await axios.get(`${endpoint()}/pdf`, { params: params(), responseType: 'blob' });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      setError(handleApiError(err, 'Failed to preview invoice PDF'));
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
  };

  const clearForm = () => {
    setForm({
      invoiceNumber: '', invoiceDate: '', plantId: '', startDate: '', endDate: '',
      invoiceFor: '', billTo: '',
    });
    setInvoice(null);
    setSearched(false);
    setError('');
  };

  const lines = invoice?.lines || [];

  // The sheet prints the date only when it changes, so a day's tickets read as one block. Lines
  // arrive in date order from the backend, so comparing with the previous row is enough.
  const dateFor = (line, index) =>
    index > 0 && lines[index - 1].ticketDate === line.ticketDate ? '' : formatDate(line.ticketDate);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Plant Invoice</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Bill a plant for printed weigh tickets — one line per ticket, so each row ties to the ticket
        behind it. (To bill a customer for Ivy field tickets, use the Field Ticket Invoice screen.)
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Invoice Number"
              value={form.invoiceNumber}
              onChange={handleChange('invoiceNumber')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Invoice Date"
              type="date"
              value={form.invoiceDate}
              onChange={handleChange('invoiceDate')}
              InputLabelProps={{ shrink: true }}
              helperText="Defaults to end date"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Plant"
              select
              required
              value={form.plantId}
              onChange={handleChange('plantId')}
              fullWidth
            >
              <MenuItem value=""><em>Select plant</em></MenuItem>
              {visiblePlants(role, plants).map((plant) => (
                <MenuItem key={plant.id} value={plant.id}>
                  {plant.name || plant.code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              required
              value={form.startDate}
              onChange={handleChange('startDate')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              required
              value={form.endDate}
              onChange={handleChange('endDate')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              label="Invoice for"
              value={form.invoiceFor}
              onChange={handleChange('invoiceFor')}
              placeholder="e.g. MARTIN MARIETA - PAYTON FARMS"
              helperText="The job line on the invoice. Leave blank to derive it from the tickets."
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              label="Bill To"
              value={form.billTo}
              onChange={handleChange('billTo')}
              placeholder="e.g. REUSABLE ORGANIC ENERGY"
              helperText="Who is billed — not always the plant. Blank uses the customer on the tickets."
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: { xs: 0, md: 1 } }}
            >
              <Button variant="contained" onClick={generate}>Generate</Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={preview}
                disabled={!invoice || lines.length === 0}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={downloadPdf}
                disabled={!invoice || lines.length === 0}
              >
                PDF
              </Button>
              <Button variant="text" onClick={clearForm} sx={{ ml: { sm: 'auto' } }}>Clear</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}
      {searched && !loading && !error && invoice && lines.length === 0 && (
        <NoMatchingRecords sx={{ mb: 2 }} />
      )}

      {invoice && lines.length > 0 && (
        <>
          {/* Mirrors the printed sheet's header so the preview reads like the PDF it produces. */}
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#404040', color: '#fff', px: 2, py: 1.25 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Ivy&apos;s Trucking Corp</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Invoice for:</Typography>
                <Typography sx={{ fontWeight: 700 }}>{invoice.invoiceFor || '—'}</Typography>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="body2">
                    <b>Bill To:</b> {invoice.billTo || '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {invoice.plantName} · {formatDate(invoice.startDate)} to {formatDate(invoice.endDate)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="body2">Invoice #: {invoice.invoiceNumber || '-'}</Typography>
                  <Typography variant="body2">Invoice Date: {formatDate(invoice.invoiceDate)}</Typography>
                  <Typography variant="body2" color="text.secondary">Tickets: {invoice.ticketCount}</Typography>
                </Box>
              </Stack>
            </Box>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              {/* Columns match the printed sheet: Date · Truck # · Ticket No · Quantity · Price · Total.
                  The job code is shown once in the header ("Invoice for"), not repeated per row. */}
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Truck #</TableCell>
                  <TableCell>Ticket No</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow
                    key={`${line.ticketNo}-${index}`}
                    sx={{ bgcolor: index % 2 ? '#F7F7F7' : 'inherit' }}
                  >
                    <TableCell>{dateFor(line, index)}</TableCell>
                    <TableCell>{line.truckNo}</TableCell>
                    <TableCell>{line.ticketNo}</TableCell>
                    <TableCell align="right">{number(line.totalTons)}</TableCell>
                    <TableCell align="right">{money(line.rate)}</TableCell>
                    <TableCell align="right">{money(line.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3}>
                    <strong>Total</strong>{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      ({invoice.ticketCount} ticket{invoice.ticketCount === 1 ? '' : 's'})
                    </Typography>
                  </TableCell>
                  <TableCell align="right"><strong>{number(invoice.totalTons)}</strong></TableCell>
                  <TableCell />
                  <TableCell align="right"><strong>{money(invoice.total)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <PdfPreviewDialog
        open={!!previewUrl}
        onClose={closePreview}
        url={previewUrl}
        title="Plant Invoice — Preview"
        fileName={pdfName()}
      />
    </Box>
  );
}
